import React, { useState, useEffect, useCallback } from 'react';
import swal from '@sweetalert/with-react';
import mathlive from 'mathlive';
import { toast } from 'react-toastify';
import NavBar from './subcomponents/Navbar';
import spinner from '../../images/paul_loading.gif';
import Instructions from './subcomponents/Instructions';
import Header from './subcomponents/Header';
import history from '../../utils/history';
import { gateway } from '../../utils/api';
import { checkIsVertibular, toastDefaultOptions } from '../../utils/functions';
import { Container, ContainerBtn, Loading } from './styles';
import { LitButton } from '../shared/LitButton';
import ViewEditor from '../shared/ViewEditor';
import Summary from './subcomponents/Summary';
import NavBarSuperior from '../NavBarSuperior/NavBarSuperior';
import ButtonActions from './subcomponents/ButtonActions';

function AssessmentWaitingRoom({ location }) {
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState(null);
  const [data, setData] = useState(null);
  const [attempts, setAttempts] = useState([]);
  const [type, setType] = useState(null);

  function notify(text, duration = 4000) {
    toast(text, {
      ...toastDefaultOptions,
      position: toast.POSITION.TOP_RIGHT,
      autoClose: duration
    });
  }

  function assessmentScheduledInvalidAlert() {
    swal({
      title: `Avaliação indisponível`,
      text: `Fora do prazo para realização da avaliação`,
      buttons: {
        confirm: {
          text: 'Ok',
          value: true,
          visible: true,
          className: 'btn-alert btn-alert-confirm-onboarding'
        }
      }
    }).then(() => {
      window.close();
    });
  }

  function goToAssessment() {
    try {
      const { hasEventValid = null } = data;

      if (hasEventValid) {
        if (!hasEventValid.isStudentAbleToDoAssessment) {
          assessmentScheduledInvalidAlert();
          return;
        }
      }
    } catch (error) {
      console.error('hasEventValid', error);
    }

    const params = new URLSearchParams(location.search);
    const assessmentType = params.get('type');
    setType(assessmentType);
    const courseCode = params.get('courseCrCode');
    const isVestibular = checkIsVertibular();
    const ucCrCode = params.get('ucCrCode');

    if (data.availabitity === 'Indisponível' && !isVestibular) {
      notify('Avaliação indisponível! Quantidade de tentativas excedidas');
      return;
    }

    let baseRedirectUrl = '';

    if (assessmentType === 'FORUM') {
      baseRedirectUrl = `assessment-forum?code=${code}`;
    } else {
      baseRedirectUrl = `assessment?code=${code}`;
    }

    baseRedirectUrl += assessmentType ? `&type=${assessmentType}` : '';
    baseRedirectUrl += courseCode ? `&course=${courseCode}` : '';

    if(ucCrCode) {
      baseRedirectUrl += `&ucCrCode=${ucCrCode}`;
    }

    history.push(baseRedirectUrl);
  }

  function handleErrorAlert() {
    swal({
      title: `Ops, ocorreu um erro.`,
      text: `Não foi possível buscar os dados dessa avaliação.`,
      buttons: {
        confirm: {
          text: 'Continuar',
          value: true,
          visible: true,
          className: 'btn-alert btn-alert-confirm-onboarding'
        }
      }
    }).then(() => {
      window.close();
    });
  }

  const renderText = useCallback(text => {
    // Pattern to identify latex expression like {\infty}
    const latexPattern = /\\\w+({.*})?/g;
    const hasLatex = latexPattern.test(text);
    if (hasLatex) {
      return (
        <span
          style={{ marginLeft: 5 }}
          dangerouslySetInnerHTML={{
            __html: mathlive.latexToMarkup(text)
          }}
        />
      );
    }

    const formattedData = `<p>${text || 'Sem descrição'}</p>`;
    return <ViewEditor data={formattedData.replace(/\n/g, '<br/>')} />;
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const assessmentCode = params.get('code');
    const courseCode = params.get('courseCrCode');

    async function loadAssessmentInfo() {
      try {
        const response = await gateway.get(
          `/webbff/lit_app_web/evaluation/${assessmentCode}/instructions?courseCode=${courseCode}`
        );
        return response;
      } catch (error) {
        handleErrorAlert();
        throw error;
      }
    }

    async function getAssessmentAttempts() {
      try {
        const url = `webbff/lit_app_web/evaluation/${assessmentCode}/summary`;
        const attempts = await gateway.get(url);
        return attempts;
      } catch (error) {
        console.error('Erro ao carregar tentativas da avaliação:', error);
        return [];
      }
    }

    async function getEvaluationBoard() {
      try {
        await gateway.get(
          `/webbff/lit_app_web/course/${courseCode}/evaluations`
        );
      } catch (error) {
        console.error('getEvaluationBoard', error);
      }
    }

    async function loadAllData() {
      try {
        const [assessmentData, attemptsData] = await Promise.all([
          loadAssessmentInfo(),
          getAssessmentAttempts()
        ]);
        setData(assessmentData);
        setAttempts(attemptsData);
        if (assessmentData) {
          getEvaluationBoard();
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error('Erro ao carregar dados:', error);
      }
    }
    if (assessmentCode) {
      setCode(assessmentCode);
      loadAllData();
    } else {
      handleErrorAlert();
    }
  }, []);

  return (
    <>
      {loading && (
        <Loading>
          <img
            alt="loading"
            style={{
              maxHeight: '85%',
              animation: 'loading-spin infinite 10s linear'
            }}
            src={spinner}
          />
        </Loading>
      )}

      {!loading && data && (
        <>
          <NavBarSuperior courses onlyLogo />
          <Container>
            <Header name={data.name} type={data.type} />

            <div
              style={{
                paddingLeft: 5,
                paddingRight: 5
              }}
            >
              {renderText(data.evaluationRules)}
            </div>

            {data.type !== 'SELF_ASSESSMENT' && (
              <Instructions
                questionCount={data.questionCount}
                totalAverageTime={data.totalAverageTime}
                averageTimePerQuestion={data.averageTimePerQuestion}
                timeLimit={data.timeLimit}
                totalAttempts={data.totalAttempts}
                minimumCriterion={data.minimumCriterion}
                evaluationRules={data.evaluationRules}
                medias={data.medias}
                type={data.type}
                tags={data.tags}
                status={data.status}
                attemptsMade={data.attemptsMade}
                availabitity={data.availabitity}
              />
            )}
            <ButtonActions
              data={data}
              goToAssessment={goToAssessment}
              loading={loading}
              attempts={attempts}
              code={code}
              type={type}
            />
          </Container>
        </>
      )}
    </>
  );
}

export default AssessmentWaitingRoom;
